/** @jsx jsx */
import { Fragment, FC, useMemo } from 'react'
import { jsx } from '@emotion/core'
import { useStaticQuery, graphql } from 'gatsby'

import Helmet from '../../components/HelmetHelper'
import Container from '../../components/Container'
import Text, { TEXT_SIZE_SMALL } from '../../components/content/Text'
import {
	THEME_TAUPE,
	THEME_WHITE,
	colors,
	media,
	THEME_GREEN,
} from '../../constants'
import Slide, {
	SLIDE_LAYOUT_MOCK_FULL_SCREEN,
	SLIDE_LAYOUT_FULL_BLEED_IMAGE,
	SLIDE_LAYOUT_DEFAULT,
} from '../../components/Slide'
import {
	LayoutSplitHeading,
	LayoutIndented,
	LayoutCol2,
	LayoutVerticalCadence,
	LAYOUT_V_CADENCE_SIZE_MEDIUM,
} from '../../components/content/Layout'
import FullBleedImage from '../../components/content/FullBleedImage'
import { InlineLink, StandaloneLink } from '../../components/content/Link'
import { provideAccentTheme } from '../../context/AccentThemeContext'
import Highlight from '../../components/content/Highlight'

import Showcase, {
	SHOWCASE_LAYOUT_1UP,
	SHOWCASE_LAYOUT_2UP,
	SHOWCASE_LAYOUT_4UP,
} from '../../components/content/Showcase'
import VideoLoop, {
	VIDEO_CLIP_AUTOPLAY,
} from '../../components/content/VideoLoop'

import { caseStudyLinkData } from '../../workData'
import { Row } from '../../components/Grid'

import gifA from '../../images/coinbase/FA_CS_Coinbase_Loop_Assets-Trade-Ethereum.gif'
import gifB from '../../images/coinbase/Coinbase-Institutional.gif'
import gifC from '../../images/coinbase/FA_CS_Coinbase_Loop_Secure-Account.gif'

const nextPage = caseStudyLinkData.coinbase.next

type Props = {
	accentTheme: string
}

const CoinbasePage: FC<Props> = ({ accentTheme }) => {
	const data = useStaticQuery(graphql`
		query {
			graphics: allFile(
				filter: { relativeDirectory: { eq: "coinbase/graphics" } }
				sort: { fields: name, order: ASC }
			) {
				nodes {
					name
					childImageSharp {
						fluid(maxWidth: 2048, quality: 100) {
							...GatsbyImageSharpFluid_noBase64
						}
					}
				}
			}
		}
	`)

	const graphicsByName = useMemo(() => {
		return (data.graphics.nodes as { name: string }[]).reduce(
			(result, node) => {
				return {
					...result,
					[node.name]: node,
				}
			},
			{}
		)
	}, [data]) as Record<string, any>

	return (
		<Fragment>
			<Helmet>
				<title>Coinbase</title>
				<meta content="Coinbase Case Study" property="og:title" />
				<meta content="Coinbase Case Study" name="twitter:title" />
				<meta
					property="og:description"
					content="Building a bold new frontier for crypto."
				/>
				<meta
					name="description"
					content="Building a bold new frontier for crypto."
				/>
				<meta
					content="https://freeassociation.com/share/coinbase.jpg"
					property="og:image"
				/>
				<meta
					content="https://freeassociation.com/share/coinbase.jpg"
					property="twitter:image:src"
				/>
			</Helmet>
			<Slide layout={SLIDE_LAYOUT_MOCK_FULL_SCREEN}>
				<Container>
					<LayoutIndented>
						<Text>
							<h1 className="--eyebrow">Coinbase</h1>
							<p className="--headline">
								Building a bold new frontier for crypto.
							</p>
						</Text>
					</LayoutIndented>
				</Container>
			</Slide>
			<Slide>
				<Container>
					<LayoutVerticalCadence size={LAYOUT_V_CADENCE_SIZE_MEDIUM}>
						<LayoutSplitHeading title="Overview">
							<Text defaultParagraph={TEXT_SIZE_SMALL}>
								<p>
									Coinbase is the world’s leading cryptocurrency exchange, known
									for its trustworthy and easy-to-use trading app. With crypto,
									web3 and NFTs rapidly becoming driving forces in the global
									marketplace, Coinbase saw the opportunity to enter a new stage
									of hyper-growth and reposition itself as the center of the
									broader crypto-economy for millions of new users.
								</p>
								<p>
									Free Association partnered closely with internal teams to
									define a bold and ambitious vision for unifying design across
									Coinbase’s product ecosystem, followed by a year-long
									partnership to help rollout the system and support critical
									product growth initiatives across the entire organization.
								</p>
							</Text>
						</LayoutSplitHeading>
						<LayoutSplitHeading title="Services">
							<Text defaultParagraph={TEXT_SIZE_SMALL}>
								<ul className="--unstyled-mediumUp --col-2">
									<li>User Experience Design</li>
									<li>Visual Design</li>
									<li>Prototyping</li>
									<li>Brand & Visual Systems</li>
									<li>Usage Guidelines</li>
									<li>UI Libraries</li>
								</ul>
							</Text>
						</LayoutSplitHeading>
					</LayoutVerticalCadence>
				</Container>
			</Slide>
			<Slide
				prefferedTheme={THEME_TAUPE}
				style={{
					[media.small]: {
						paddingTop: '3.5rem',
						paddingBottom: '3.5rem',
						marginBottom: '3.5rem',
					},
				}}
			>
				<Container>
					<VideoLoop width={2540} height={1440} mode={VIDEO_CLIP_AUTOPLAY}>
						<source src="/media/coinbase/FA_CS_Coinbase_Sizzle-Wide.mp4" />
					</VideoLoop>
				</Container>
			</Slide>
			<Slide>
				<Container>
					<LayoutVerticalCadence size={LAYOUT_V_CADENCE_SIZE_MEDIUM}>
						<LayoutIndented>
							<Text>
								<p>
									In the first phase of our multi-year engagement, we embarked
									on a north star vision sprint to define a new unified app
									architecture and refreshed visual language that would inform
									Coinbase’s next generation design system.
								</p>
							</Text>
						</LayoutIndented>
						<LayoutCol2 verticalCadenceSize={LAYOUT_V_CADENCE_SIZE_MEDIUM}>
							<Highlight title="Unified Architecture">
								<Text defaultParagraph={TEXT_SIZE_SMALL}>
									<p className="--grey">
										In order to create a scalable framework for future UX
										evolution, we consolidated and streamlined core web surfaces
										and use cases for the web and mobile apps, and built new new
										entry points for users to explore the the broader Coinbase
										ecosystem.
									</p>
								</Text>
							</Highlight>
							<Highlight title="Visual Expression">
								<Text defaultParagraph={TEXT_SIZE_SMALL}>
									<p className="--grey">
										We developed a fresh, distinctive future look and feel that
										could unite all Coinbase products while still serving the
										unique needs of different user groups, from crypto-novices
										all the way to savvy institutional brokerage managers.
									</p>
								</Text>
							</Highlight>
						</LayoutCol2>
					</LayoutVerticalCadence>
				</Container>
			</Slide>

			<Slide
				prefferedTheme={THEME_TAUPE}
				layout={SLIDE_LAYOUT_FULL_BLEED_IMAGE}
			>
				<div>
					<Container>
						<Row style={{}}>
							<img
								src={gifA}
								alt="Coinbase"
								css={{
									width: '100%',
									display: 'block',
								}}
							/>
						</Row>
					</Container>
				</div>
			</Slide>
			<Slide
				prefferedTheme={THEME_WHITE}
				layout={SLIDE_LAYOUT_FULL_BLEED_IMAGE}
			>
				<div
					css={{
						background: colors.taupe,
					}}
				>
					<Container>
						<FullBleedImage
							fluidImageData={graphicsByName.coinbase_01.childImageSharp.fluid}
						/>
					</Container>
				</div>
			</Slide>

			<Slide
				prefferedTheme={THEME_TAUPE}
				style={{
					[media.small]: {
						paddingTop: '3.5rem',
						paddingBottom: '3.5rem',
					},
				}}
			>
				<Container maxWidth="75rem">
					<Showcase
						layout={SHOWCASE_LAYOUT_4UP}
						images={[
							graphicsByName.coinbase_02_01.childImageSharp.fluid,
							graphicsByName.coinbase_02_02.childImageSharp.fluid,
							graphicsByName.coinbase_02_03.childImageSharp.fluid,
							graphicsByName.coinbase_02_04.childImageSharp.fluid,
						]}
						chrome={false}
					/>
				</Container>
			</Slide>
			<Slide
				prefferedTheme={THEME_TAUPE}
				style={{
					[media.small]: {
						paddingTop: '3.5rem',
						paddingBottom: '3.5rem',
					},
				}}
			>
				<Container maxWidth="75rem">
					<Showcase
						chrome={false}
						layout={SHOWCASE_LAYOUT_2UP}
						images={[
							graphicsByName.coinbase_03.childImageSharp.fluid,
							graphicsByName.coinbase_04.childImageSharp.fluid,
						]}
					/>
				</Container>
			</Slide>
			<Slide>
				<Container>
					<LayoutVerticalCadence size={LAYOUT_V_CADENCE_SIZE_MEDIUM}>
						<LayoutIndented>
							<Text>
								<p>
									After the vision phase, we were brought in to work
									side-by-side Coinbase vertical teams to implement the new
									design system across key product areas and new growth
									initiatives.
								</p>
							</Text>
						</LayoutIndented>
					</LayoutVerticalCadence>
				</Container>
			</Slide>
			<Slide
				prefferedTheme={THEME_WHITE}
				layout={SLIDE_LAYOUT_FULL_BLEED_IMAGE}
			>
				<div
					css={{
						background: colors.taupe,
					}}
				>
					<FullBleedImage
						style={{
							maxWidth: 'none',
						}}
						objectFit="cover"
						fluidImageData={graphicsByName.coinbase_05.childImageSharp.fluid}
					/>
				</div>
			</Slide>

			<Slide>
				<Container>
					<LayoutVerticalCadence size={LAYOUT_V_CADENCE_SIZE_MEDIUM}>
						<LayoutIndented>
							<Text>
								<p>
									Over the course of 12 months, we helped design, test, and ship
									more than 20 high-profile initiatives across the Coinbase
									product ecosystem.
								</p>
							</Text>
						</LayoutIndented>
						<LayoutCol2 verticalCadenceSize={LAYOUT_V_CADENCE_SIZE_MEDIUM}>
							<Highlight title="Logged Out Web">
								<Text defaultParagraph={TEXT_SIZE_SMALL}>
									<p className="--grey">
										We led the design, content strategy, and production for a
										comprehensive sister design system to power the logged out
										web experience for all of Coinbase.com, driving acquisition
										strategies and delivering thought-leadership content to the
										market.
									</p>
								</Text>
							</Highlight>
							<Highlight title="Exchange Platform">
								<Text defaultParagraph={TEXT_SIZE_SMALL}>
									<p className="--grey">
										Extending our proof of concept work, we completely
										redesigned Coinbase’s advanced trading platform Exchange,
										leveraging variants of the new system and adapting them for
										dense, data-rich displays to serve the unique needs of
										institutional customers.
									</p>
								</Text>
							</Highlight>
							<Highlight title="DeFi Identity & Profiles">
								<Text defaultParagraph={TEXT_SIZE_SMALL}>
									<p className="--grey">
										We partnered with the DeFi team to design a product
										extension allowing users to carry a portable web3 identity
										and profile across decentralized marketplaces, solving the
										balance of trust and privacy.
									</p>
								</Text>
							</Highlight>
							<Highlight title="Risk & Compliance Tools">
								<Text defaultParagraph={TEXT_SIZE_SMALL}>
									<p className="--grey">
										We designed and prototyped designs for a no-code rule
										builder, allowing internal analysts and compliance managers
										to create new risk and permission rules without knowledge of
										complex syntax language.
									</p>
								</Text>
							</Highlight>
							<Highlight title="Growth & Emerging Markets">
								<Text defaultParagraph={TEXT_SIZE_SMALL}>
									<p className="--grey">
										We helped launch critical UX updates to support
										internationalization and localization of new user onboarding
										and payment verification flows for emerging markets,
										including Brazil and Japan.
									</p>
								</Text>
							</Highlight>

							<Highlight title="Incentives & Rewards">
								<Text defaultParagraph={TEXT_SIZE_SMALL}>
									<p className="--grey">
										Designed and launched several key reward and incentive
										programs for new Coinbase users, supported by a global
										architecture to house them and a shared component library
										powering their creation.
									</p>
								</Text>
							</Highlight>
						</LayoutCol2>
					</LayoutVerticalCadence>
				</Container>
			</Slide>
			<Slide
				prefferedTheme={THEME_WHITE}
				layout={SLIDE_LAYOUT_FULL_BLEED_IMAGE}
			>
				<div
					css={{
						background: colors.taupe,
					}}
				>
					<Container>
						<FullBleedImage
							fluidImageData={graphicsByName.coinbase_06.childImageSharp.fluid}
						/>
					</Container>
				</div>
			</Slide>

			<Slide prefferedTheme={THEME_TAUPE} layout={SLIDE_LAYOUT_DEFAULT}>
				<div>
					<Container>
						<Row style={{}}>
							<img
								src={gifB}
								alt="Coinbase"
								css={{
									width: '100%',
									display: 'block',
								}}
							/>
						</Row>
					</Container>
				</div>
			</Slide>
			<Slide
				prefferedTheme={THEME_TAUPE}
				style={{
					[media.small]: {
						paddingTop: '3.5rem',
						paddingBottom: '3.5rem',
					},
				}}
			>
				<Container maxWidth="75rem">
					<Showcase
						chrome={false}
						layout={SHOWCASE_LAYOUT_4UP}
						images={[
							graphicsByName.coinbase_07_01.childImageSharp.fluid,
							graphicsByName.coinbase_07_02.childImageSharp.fluid,
							graphicsByName.coinbase_07_03.childImageSharp.fluid,
							graphicsByName.coinbase_07_04.childImageSharp.fluid,
						]}
					/>
				</Container>
			</Slide>
			<Slide
				prefferedTheme={THEME_TAUPE}
				layout={SLIDE_LAYOUT_FULL_BLEED_IMAGE}
			>
				<div>
					<Container>
						<Row style={{}}>
							<img
								src={gifC}
								alt="Coinbase"
								css={{
									width: '100%',
									display: 'block',
								}}
							/>
						</Row>
					</Container>
				</div>
			</Slide>
			<Slide
				prefferedTheme={THEME_TAUPE}
				style={{
					[media.small]: {
						paddingTop: '3.5rem',
						paddingBottom: '3.5rem',
					},
				}}
			>
				<Container maxWidth="75rem">
					<Showcase
						chrome={false}
						layout={SHOWCASE_LAYOUT_4UP}
						images={[
							graphicsByName.coinbase_08_01.childImageSharp.fluid,
							graphicsByName.coinbase_08_02.childImageSharp.fluid,
							graphicsByName.coinbase_08_03.childImageSharp.fluid,
							graphicsByName.coinbase_08_04.childImageSharp.fluid,
						]}
					/>
				</Container>
			</Slide>
			<Slide
				prefferedTheme={THEME_TAUPE}
				style={{
					[media.small]: {
						paddingTop: '3.5rem',
						paddingBottom: '3.5rem',
					},
				}}
			>
				<Container>
					<Showcase
						chrome={false}
						layout={SHOWCASE_LAYOUT_1UP}
						images={[graphicsByName.coinbase_09.childImageSharp.fluid]}
					/>
				</Container>
			</Slide>
			<Slide>
				<Container>
					<LayoutIndented>
						<Text>
							<p className="--eyebrow">Next</p>
							<p className="--headline">
								<StandaloneLink to={nextPage.to!}>
									{nextPage.title}
								</StandaloneLink>
							</p>
							<p className="--small">
								<InlineLink to="/work">All Work</InlineLink>
							</p>
						</Text>
					</LayoutIndented>
				</Container>
			</Slide>
		</Fragment>
	)
}

export default provideAccentTheme(CoinbasePage, THEME_GREEN)
